import type { AxiosProgressEvent } from 'axios'
import { z } from 'zod'

import {
  fileUploadConfigSchema,
  uploadedFileSchema,
  fileMetadataSchema,
  type FileUploadConfig,
  type UploadedFile,
  type FileMetadata,
  type FileUploadOptions,
} from '@/types/api/file'

import { apiClient } from './client'

// ── Shared multipart helpers ─────────────────────────────────────────────────

/**
 * Multipart requests must override the client's default JSON content type —
 * otherwise axios serialises the FormData as JSON. Axios still lets the browser
 * set the proper multipart boundary for FormData bodies.
 */
/** Adds the optional compression fields of an upload to the multipart body. */
export function appendEncodingFields(formData: FormData, options?: FileUploadOptions) {
  if (options?.contentEncoding) {
    formData.append('contentEncoding', options.contentEncoding)
    if (options.originalSize != null) formData.append('originalSize', String(options.originalSize))
  }
}

export function multipartRequestConfig(options?: FileUploadOptions) {
  return {
    headers: { 'Content-Type': 'multipart/form-data' },
    signal: options?.signal,
    onUploadProgress: (event: AxiosProgressEvent) => {
      if (!options?.onProgress) return
      const total = event.total ?? 0
      if (total > 0) {
        options.onProgress(Math.min(100, Math.round((event.loaded / total) * 100)))
      }
    },
  }
}

// ── Config & metadata ────────────────────────────────────────────────────────

/** Global upload policy (max size + allowed file types). */
export async function getFileUploadConfig(): Promise<FileUploadConfig> {
  const response = await apiClient.get('/files/config')
  return fileUploadConfigSchema.parse(response.data)
}

/** Batch metadata + fresh pre-signed download URLs, scoped to the workspace. */
export async function getFileMetadata(keys: string[]): Promise<FileMetadata[]> {
  if (keys.length === 0) return []
  const response = await apiClient.post('/files/metadata', { keys })
  return z.array(fileMetadataSchema).parse(response.data)
}

// ── Uploads ──────────────────────────────────────────────────────────────────

/** Uploads a document for a FILE_UPLOAD form field; the returned key becomes the value. */
export async function uploadRecordValueFile(
  recordGroupId: string,
  formFieldId: string,
  file: File,
  options?: FileUploadOptions
): Promise<UploadedFile> {
  const formData = new FormData()
  formData.append('formFieldId', formFieldId)
  formData.append('file', file)
  appendEncodingFields(formData, options)

  const response = await apiClient.post(
    `/record-values/upload/${recordGroupId}`,
    formData,
    multipartRequestConfig(options)
  )
  return uploadedFileSchema.parse(response.data)
}
